import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../Auth/AuthContext';
import { Link } from 'react-router-dom';
import './AnalysisReport.css';
import { getLatestUserTestResults, saveAnalysisReport } from '../../utils/supabase';
import { generateEQAnalysisReport } from '../../utils/eqAnalysis';

// 多维雷达图表组件
const RadarChart = ({ data, labels, title }) => {
  const canvasRef = React.useRef(null);
  
  useEffect(() => {
    if (!canvasRef.current || !data || !labels) return;
    
    const ctx = canvasRef.current.getContext('2d');
    const centerX = canvasRef.current.width / 2;
    const centerY = canvasRef.current.height / 2;
    const radius = Math.min(centerX, centerY) * 0.8;
    
    // 清除画布
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    
    // 绘制背景网格
    const levels = 5;
    ctx.strokeStyle = 'rgba(200, 200, 200, 0.3)';
    ctx.fillStyle = 'rgba(220, 220, 255, 0.1)';
    
    for (let i = 1; i <= levels; i++) {
      ctx.beginPath();
      const levelRadius = (radius / levels) * i;
      ctx.arc(centerX, centerY, levelRadius, 0, Math.PI * 2);
      ctx.stroke();
    }
    
    // 绘制轴线
    const angles = [];
    ctx.strokeStyle = 'rgba(150, 150, 150, 0.4)';
    ctx.beginPath();
    for (let i = 0; i < labels.length; i++) {
      const angle = (Math.PI * 2 * i) / labels.length - Math.PI / 2;
      angles.push(angle);
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(x, y);
      
      // 绘制标签
      const labelX = centerX + (radius + 20) * Math.cos(angle);
      const labelY = centerY + (radius + 20) * Math.sin(angle);
      ctx.font = '12px Arial';
      ctx.fillStyle = '#333';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(labels[i], labelX, labelY);
    }
    ctx.stroke();
    
    // 绘制数据区域
    ctx.beginPath();
    for (let i = 0; i < data.length; i++) {
      const value = data[i] / 100; // 假设数据范围是0-100
      const angle = angles[i];
      const x = centerX + radius * value * Math.cos(angle);
      const y = centerY + radius * value * Math.sin(angle);
      
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.lineTo(centerX + radius * (data[0] / 100) * Math.cos(angles[0]), 
               centerY + radius * (data[0] / 100) * Math.sin(angles[0]));
    ctx.fillStyle = 'rgba(79, 70, 229, 0.2)';
    ctx.fill();
    
    // 绘制数据点和连线
    ctx.beginPath();
    for (let i = 0; i < data.length; i++) {
      const value = data[i] / 100;
      const angle = angles[i];
      const x = centerX + radius * value * Math.cos(angle);
      const y = centerY + radius * value * Math.sin(angle);
      
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.lineTo(centerX + radius * (data[0] / 100) * Math.cos(angles[0]), 
               centerY + radius * (data[0] / 100) * Math.sin(angles[0]));
    ctx.strokeStyle = 'rgba(79, 70, 229, 0.8)';
    ctx.lineWidth = 2;
    ctx.stroke();
    
    // 绘制数据点
    for (let i = 0; i < data.length; i++) {
      const value = data[i] / 100;
      const angle = angles[i];
      const x = centerX + radius * value * Math.cos(angle);
      const y = centerY + radius * value * Math.sin(angle);
      
      ctx.beginPath();
      ctx.arc(x, y, 5, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(79, 70, 229, 1)';
      ctx.fill();
    }
    
  }, [data, labels]);
  
  return (
    <div className="radar-chart">
      <h3 className="chart-title">{title}</h3>
      <canvas ref={canvasRef} width={300} height={300}></canvas>
    </div>
  );
};

// 情绪曲线图
const EmotionLineChart = ({ data, title }) => {
  const canvasRef = React.useRef(null);
  
  useEffect(() => {
    if (!canvasRef.current || !data) return;
    
    const ctx = canvasRef.current.getContext('2d');
    const width = canvasRef.current.width;
    const height = canvasRef.current.height;
    const padding = 40;
    
    // 清除画布
    ctx.clearRect(0, 0, width, height);
    
    // 绘制坐标轴
    ctx.beginPath();
    ctx.moveTo(padding, padding);
    ctx.lineTo(padding, height - padding);
    ctx.lineTo(width - padding, height - padding);
    ctx.strokeStyle = '#ccc';
    ctx.stroke();
    
    // X轴标签
    const labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    const xStep = (width - 2 * padding) / (labels.length - 1);
    
    ctx.font = '12px Arial';
    ctx.fillStyle = '#666';
    ctx.textAlign = 'center';
    
    labels.forEach((label, i) => {
      const x = padding + i * xStep;
      ctx.fillText(label, x, height - padding + 20);
    });
    
    // Y轴标签
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    
    for (let i = 0; i <= 5; i++) {
      const y = height - padding - (i * (height - 2 * padding)) / 5;
      ctx.fillText((i * 20).toString(), padding - 10, y);
      
      // 水平网格线
      ctx.beginPath();
      ctx.moveTo(padding, y);
      ctx.lineTo(width - padding, y);
      ctx.strokeStyle = 'rgba(200, 200, 200, 0.3)';
      ctx.stroke();
    }
    
    // 绘制数据线
    const maxValue = 100;
    ctx.beginPath();
    data.forEach((value, i) => {
      const x = padding + i * xStep;
      const y = height - padding - ((value / maxValue) * (height - 2 * padding));
      
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    
    // 渐变填充
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, 'rgba(79, 70, 229, 0.8)');
    gradient.addColorStop(1, 'rgba(79, 70, 229, 0)');
    
    ctx.strokeStyle = 'rgba(79, 70, 229, 1)';
    ctx.lineWidth = 3;
    ctx.stroke();
    
    // 填充区域
    ctx.lineTo(padding + (data.length - 1) * xStep, height - padding);
    ctx.lineTo(padding, height - padding);
    ctx.fillStyle = gradient;
    ctx.fill();
    
    // 绘制数据点
    data.forEach((value, i) => {
      const x = padding + i * xStep;
      const y = height - padding - ((value / maxValue) * (height - 2 * padding));
      
      ctx.beginPath();
      ctx.arc(x, y, 6, 0, Math.PI * 2);
      ctx.fillStyle = '#fff';
      ctx.fill();
      ctx.strokeStyle = 'rgba(79, 70, 229, 1)';
      ctx.lineWidth = 2;
      ctx.stroke();
    });
    
  }, [data]);
  
  return (
    <div className="emotion-line-chart">
      <h3 className="chart-title">{title}</h3>
      <canvas ref={canvasRef} width={300} height={250}></canvas>
    </div>
  );
};

// 能力指标条形图
const SkillBarChart = ({ scores, labels }) => {
  return (
    <div className="skill-bar-chart">
      <h3 className="chart-title">Skill Performance</h3>
      <div className="skill-bars">
        {scores.map((score, index) => (
          <div key={index} className="skill-bar-item">
            <div className="skill-label">{labels[index]}</div>
            <div className="skill-bar-container">
              <div 
                className="skill-bar-fill" 
                style={{ 
                  width: `${score}%`,
                  background: `linear-gradient(to right, hsl(${index * 30}, 70%, 60%), hsl(${index * 30}, 80%, 70%))`
                }}
              ></div>
              <span className="skill-score">{score}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// 情商特质卡片
const TraitCard = ({ title, description, score, icon }) => {
  return (
    <div className="trait-card">
      <div className="trait-header">
        <div className="trait-icon">{icon}</div>
        <div className="trait-title-container">
          <h3 className="trait-title">{title}</h3>
          <div className="trait-score-badge">{score}/100</div>
        </div>
      </div>
      <p className="trait-description">{description}</p>
    </div>
  );
};

function AnalysisReport() {
  const { currentUser } = useAuth();
  // eslint-disable-next-line no-unused-vars
  const [userData, setUserData] = useState(currentUser || {});
  const [testResults, setTestResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fallback to localStorage if needed
  const loadLocalTestResults = useCallback(() => {
    try {
      // Get test data from localStorage
      const eqTestResult = localStorage.getItem('eqTestResult');
      const customTestResult = localStorage.getItem('customTestResult');
      const personalQuestionnaireResult = localStorage.getItem('personalQuestionnaireResult');
      
      // Parse data
      const eqData = eqTestResult ? JSON.parse(eqTestResult) : null;
      const customData = customTestResult ? JSON.parse(customTestResult) : null;
      const questionnaireData = personalQuestionnaireResult ? JSON.parse(personalQuestionnaireResult) : null;
      
      // Generate analysis report
      return generateEQAnalysisReport(eqData, questionnaireData, customData);
    } catch (error) {
      console.error('Error loading test results from localStorage:', error);
      return null;
    }
  }, []);

  // Load test results from Supabase
  const loadTestResults = useCallback(async () => {
    const loadFromLocalStorage = () => {
      try {
        // Get test data from localStorage
        const eqTestResult = localStorage.getItem('eqTestResult');
        const customTestResult = localStorage.getItem('customTestResult');
        const personalQuestionnaireResult = localStorage.getItem('personalQuestionnaireResult');
        
        // Parse data
        const eqData = eqTestResult ? JSON.parse(eqTestResult) : null;
        const customData = customTestResult ? JSON.parse(customTestResult) : null;
        const questionnaireData = personalQuestionnaireResult ? JSON.parse(personalQuestionnaireResult) : null;
        
        // Generate analysis report
        return generateEQAnalysisReport(eqData, questionnaireData, customData);
      } catch (error) {
        console.error('Error loading test results from localStorage:', error);
        return null;
      }
    };

    try {
      if (!currentUser || !currentUser.id) {
        console.log('No user logged in, using localStorage data');
        return loadFromLocalStorage();
      }
      
      console.log('Fetching test results from Supabase for user:', currentUser.id);
      const { data, error } = await getLatestUserTestResults(currentUser.id);
      
      if (error) {
        console.error('Error fetching from Supabase:', error);
        return loadFromLocalStorage();
      }
      
      if (!data.eqTestResult && !data.customTestResult && !data.questionnaireResult) {
        console.log('No test data found in Supabase, using localStorage data');
        return loadFromLocalStorage();
      }
      
      console.log('Test data found in Supabase:', data);
      
      // Generate analysis report based on retrieved data
      const report = generateEQAnalysisReport(
        data.eqTestResult,
        data.questionnaireResult,
        data.customTestResult
      );
      
      // Save the generated report back to Supabase
      if (report) {
        saveAnalysisReport(currentUser.id, report)
          .then(({ data, error }) => {
            if (error) console.error('Error saving analysis report:', error);
            else console.log('Analysis report saved successfully');
          });
      }
      
      return report;
    } catch (error) {
      console.error('Error in Supabase data loading:', error);
      return loadFromLocalStorage();
    }
  }, [currentUser]);

  // Load test results and generate analysis
  useEffect(() => {
    setLoading(true);
    
    // Simulate loading delay for better UX
    const loadData = async () => {
      try {
        // 添加错误处理和默认行为
        let results;
        try {
          results = await loadTestResults();
          console.log('Loaded test results:', results);
        } catch (loadError) {
          console.error('Error loading test results:', loadError);
          results = null;
        }
        
        if (results) {
          console.log('Using actual test results');
          setTestResults(results);
        } else {
          // 检查localStorage中是否有测试数据
          const hasEqTest = localStorage.getItem('eqTestResult');
          const hasCustomTest = localStorage.getItem('customTestResult');
          const hasQuestionnaire = localStorage.getItem('personalQuestionnaireResult');
          
          // 如果本地存储中没有任何测试数据，则提示用户完成测试
          if (!hasEqTest && !hasCustomTest && !hasQuestionnaire) {
            console.log('No test data found, redirecting to questionnaire');
            setTestResults(null); // 设置为null会触发导航到问卷
            setLoading(false);
            return;
          }
          
          // 只有在确实没有任何测试数据的情况下才使用默认数据
          console.log('No test data found, using example data as fallback');
          const exampleData = {
            overallScore: 72,
            emotionalAwareness: 75,
            emotionalRegulation: 68,
            socialPerception: 70,
            relationshipManagement: 78,
            empathy: 76,
            adaptability: 65,
            strengths: [
              'Understanding others\' perspectives and emotions',
              'Building positive relationships with others',
              'Maintaining composure in challenging situations'
            ],
            weaknesses: [
              'Recognizing subtle emotional cues in certain contexts',
              'Adapting quickly to unexpected social situations',
              'Managing emotional reactions under high stress'
            ],
            recommendations: [
              'Practice mindfulness to increase emotional awareness',
              'Engage in diverse social settings to improve adaptability',
              'Develop specific strategies for handling stressful situations'
            ],
            progressData: [62, 65, 68, 70, 71, 72],
            personalityTraits: {
              openness: { score: 72, description: 'Generally open to new experiences and ideas' },
              conscientiousness: { score: 70, description: 'Organized and reliable in most situations' },
              extraversion: { score: 65, description: 'Balanced between social engagement and solitude' },
              agreeableness: { score: 78, description: 'Considerate and cooperative with others' },
              neuroticism: { score: 40, description: 'Generally emotionally stable with occasional stress reactions' }
            },
            detailedScores: {
              'Self Awareness': 75,
              'Emotional Regulation': 68,
              'Social Perception': 70,
              'Relationship Skills': 78,
              'Empathy': 76,
              'Adaptability': 65
            }
          };
          setTestResults(exampleData);
        }
      } catch (err) {
        console.error('Error generating analysis:', err);
        setError('An error occurred while analyzing your results. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    setTimeout(loadData, 1200);
    
    return () => {
      // Cleanup if needed
    };
  }, [loadTestResults]); // 重新添加依赖以确保用户测试数据更新时重新加载

  // 情感智能等级和描述
  const getEQLevel = useMemo(() => {
    if (!testResults) return { level: 'B+', title: 'Good', description: 'Loading...' };
    
    const score = testResults.overallScore;
    
    if (score >= 90) {
      return {
        level: 'A+',
        title: 'Exceptional',
        description: 'You have exceptional emotional intelligence skills that set you apart in most social and emotional situations.'
      };
    } else if (score >= 80) {
      return {
        level: 'A',
        title: 'Excellent',
        description: 'Your emotional intelligence is excellent, allowing you to navigate most social situations with ease and understanding.'
      };
    } else if (score >= 70) {
      return {
        level: 'B+',
        title: 'Good',
        description: 'You have good emotional intelligence skills with some areas of notable strength and others that could benefit from development.'
      };
    } else if (score >= 60) {
      return {
        level: 'B',
        title: 'Above Average',
        description: 'Your emotional intelligence is above average, though there are significant areas where improvement would be beneficial.'
      };
    } else if (score >= 50) {
      return {
        level: 'C+',
        title: 'Average',
        description: 'Your emotional intelligence is average, with a balanced mix of strengths and areas needing development.'
      };
    } else if (score >= 40) {
      return {
        level: 'C',
        title: 'Below Average',
        description: 'Your emotional intelligence shows some challenges that may affect your social interactions and emotional regulation.'
      };
    } else {
      return {
        level: 'D',
        title: 'Needs Improvement',
        description: 'Your results suggest significant opportunities for development in emotional intelligence skills.'
      };
    }
  }, [testResults]);

  // 渲染加载状态
  if (loading) {
    return (
      <div className="analysis-loading">
        <div className="loader"></div>
        <p>Analyzing your emotional intelligence profile...</p>
        <p className="loading-subtext">Combining test responses and generating insights</p>
      </div>
    );
  }

  // 渲染错误状态
  if (error) {
    return (
      <div className="analysis-error">
        <h2>Error Generating Report</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Try Again</button>
      </div>
    );
  }

  // No test results available - 导航到问卷页面
  if (!testResults) {
    return (
      <div className="analysis-error">
        <h2>Complete Assessment Required</h2>
        <p>Please complete the questionnaire and custom test to generate your emotional intelligence report.</p>
        <Link to="/personal-questionnaire" className="next-step-button">Start Assessment</Link>
      </div>
    );
  }

  // 主要报告视图
  return (
    <div className="analysis-report">
      <div className="analysis-header">
        <div className="header-container">
          <div className="header-text">
            <h1 className="report-title">Emotional Intelligence Assessment</h1>
            <p className="report-subtitle">Your personalized EQ analysis based on assessment data</p>
          </div>
          <div className="overall-score-container">
            <div className="score-circle">
              <div className="score-inner">
                <span className="score-value">{testResults.overallScore}</span>
                <span className="score-max">/100</span>
              </div>
              <div className="score-label">
                <span className="eq-level">{getEQLevel.level}</span>
                <span className="eq-title">{getEQLevel.title}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="header-description">
          <p>{getEQLevel.description}</p>
        </div>
      </div>

      <div className="report-content">
        <div className="report-section eq-summary">
          <h2>Emotional Intelligence Summary</h2>
          <p className="section-description">
            Emotional intelligence is your ability to recognize, understand and manage your emotions, 
            as well as recognize, understand and influence the emotions of others.
          </p>
          
          <div className="summary-stats">
            <div className="radar-chart-container">
              <RadarChart 
                data={[
                  testResults.emotionalAwareness, 
                  testResults.emotionalRegulation, 
                  testResults.socialPerception, 
                  testResults.relationshipManagement,
                  testResults.empathy,
                  testResults.adaptability
                ]}
                labels={[
                  'Self Awareness', 
                  'Emotional Regulation', 
                  'Social Perception', 
                  'Relationship Skills',
                  'Empathy',
                  'Adaptability'
                ]}
                title="EQ Dimensions"
              />
            </div>
            
            <div className="progress-chart-container">
              <EmotionLineChart 
                data={testResults.progressData}
                title="Progress Over Time"
              />
            </div>
          </div>
        </div>
        
        <div className="report-section strengths-weaknesses">
          <div className="strengths-container">
            <h2>Your Strengths</h2>
            <ul className="strengths-list">
              {testResults.strengths.map((strength, index) => (
                <li key={index} className="strength-item">
                  <span className="list-icon">✦</span> {strength}
                </li>
              ))}
            </ul>
          </div>
          
          <div className="weaknesses-container">
            <h2>Areas for Growth</h2>
            <ul className="weaknesses-list">
              {testResults.weaknesses.map((weakness, index) => (
                <li key={index} className="weakness-item">
                  <span className="list-icon">•</span> {weakness}
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        <div className="report-section detailed-skills">
          <h2>Detailed Skill Analysis</h2>
          <SkillBarChart 
            scores={Object.values(testResults.detailedScores)}
            labels={Object.keys(testResults.detailedScores)}
          />
        </div>
        
        <div className="report-section personality-traits">
          <h2>Personality Traits</h2>
          <p className="section-description">
            Your personality traits influence how you perceive emotions and interact with others. 
            Understanding them can help you leverage your strengths and work on areas for growth.
          </p>
          
          <div className="traits-container">
            <TraitCard 
              title="Openness" 
              description={testResults.personalityTraits.openness.description}
              score={testResults.personalityTraits.openness.score}
              icon="🌟"
            />
            <TraitCard 
              title="Conscientiousness" 
              description={testResults.personalityTraits.conscientiousness.description}
              score={testResults.personalityTraits.conscientiousness.score}
              icon="📊"
            />
            <TraitCard 
              title="Extraversion" 
              description={testResults.personalityTraits.extraversion.description}
              score={testResults.personalityTraits.extraversion.score}
              icon="🗣️"
            />
            <TraitCard 
              title="Agreeableness" 
              description={testResults.personalityTraits.agreeableness.description}
              score={testResults.personalityTraits.agreeableness.score}
              icon="🤝"
            />
            <TraitCard 
              title="Emotional Stability" 
              description={testResults.personalityTraits.neuroticism.description}
              score={100 - testResults.personalityTraits.neuroticism.score}
              icon="🧘"
            />
          </div>
        </div>
        
        <div className="report-section recommendations">
          <h2>Recommendations for Improvement</h2>
          <div className="recommendations-container">
            {testResults.recommendations.map((recommendation, index) => (
              <div key={index} className="recommendation-card">
                <div className="recommendation-number">{index + 1}</div>
                <p className="recommendation-text">{recommendation}</p>
              </div>
            ))}
          </div>
        </div>
        
        <div className="report-section next-steps">
          <h2>Next Steps</h2>
          <div className="next-steps-container">
            <div className="next-step-card">
              <div className="next-step-icon">🏠</div>
              <h3>Home</h3>
              <p>Return to the homepage to explore more features and options</p>
              <Link to="/" className="next-step-button">Back to Home</Link>
            </div>
            
            <div className="next-step-card">
              <div className="next-step-icon">↻</div>
              <h3>Retake Assessment</h3>
              <p>Reassess your emotional intelligence after practicing to track progress</p>
              <Link to="/personal-questionnaire" className="next-step-button secondary">New Assessment</Link>
            </div>
            
            <div className="next-step-card">
              <div className="next-step-icon">⚙️</div>
              <h3>Advanced Scenarios</h3>
              <p>Challenge yourself with complex social situations to master your skills</p>
              <Link to="/advanced-scenarios" className="next-step-button">Advanced Practice</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AnalysisReport; 